import { useIntl } from "react-intl";
import { useRecoilValue } from "recoil";

import { FormatterService, formatterService } from "../../NumberFormatter";
import { useTheme } from "@mui/material/styles";

import ForecastChart from "./ForecastChart";
import {
  axisTicks,
  currencySymbol,
  decimal,
  money,
  NIGHTS_UNIT_KEY,
  SERIES_COLORS,
} from "./constants";
import { BacktestCurvePoint } from "./services";

export type CurveMeasure = "nb_uh" | "adr";

const compact = (formatter: FormatterService, value: number) =>
  value >= 1_000_000
    ? `${decimal(formatter, value / 1_000_000, 1)} M`
    : value >= 1_000
    ? `${decimal(formatter, value / 1_000)} k`
    : decimal(formatter, value);

/**
 * What was booked at each moment against what each method announced as the landing. The
 * realized total is flat and known — these dates are past — so the two forecasts are judged
 * by how early they meet it.
 */
const BookingCurveChart = ({
  points,
  measure,
  note,
}: {
  points: BacktestCurvePoint[];
  measure: CurveMeasure;
  note?: string;
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const formatter = useRecoilValue(formatterService);
  if (points.length === 0) return null;

  const isPrice = measure === "adr";
  // Names the measure in the subtitle and beside a room-night count; a price carries the
  // symbol the formatter puts on the value itself.
  const unit = isPrice
    ? currencySymbol(formatter)
    : intl.formatMessage({ id: NIGHTS_UNIT_KEY });

  const price = (revenue: number | null, nights: number | null) =>
    revenue !== null && nights !== null && nights > 0 ? revenue / nights : null;

  const data = points.map((point) => ({
    lead_time: point.lead_time,
    rob: isPrice ? price(point.rob_revenue, point.rob_nb_uh) : point.rob_nb_uh,
    actual: isPrice
      ? price(point.actual_revenue, point.actual_nb_uh)
      : point.actual_nb_uh,
    // Its sums are zero where it forecast nothing, which would draw a line along the axis
    // instead of leaving the gap the reader should see.
    baseline:
      point.baseline_n === 0
        ? null
        : isPrice
        ? price(point.baseline_revenue, point.baseline_nb_uh)
        : point.baseline_nb_uh,
    ml: isPrice ? price(point.ml_revenue, point.ml_nb_uh) : point.ml_nb_uh,
  }));

  const leadTimes = data.map((point) => point.lead_time);

  return (
    <ForecastChart
      title={intl.formatMessage({
        id: isPrice
          ? "ML_FORECAST_BOOKING_CURVE_ADR"
          : "ML_FORECAST_BOOKING_CURVE_NB_UH",
      })}
      subtitle={`${intl.formatMessage(
        { id: "ML_FORECAST_BOOKING_CURVE_SUBTITLE" },
        { unit }
      )}${note ? ` ${note}` : ""}`}
      data={data}
      xKey="lead_time"
      xDomain={[Math.min(...leadTimes), Math.max(...leadTimes)]}
      xTicks={axisTicks(leadTimes)}
      formatX={(value) => `J${value}`}
      formatY={(value) =>
        isPrice ? money(formatter, value) : compact(formatter, value)
      }
      formatValue={(value) =>
        isPrice
          ? money(formatter, value, 2)
          : `${decimal(formatter, value)} ${unit}`
      }
      series={[
        {
          key: "actual",
          name: intl.formatMessage({ id: "ML_FORECAST_LEGEND_ACTUAL" }),
          color: SERIES_COLORS.actual,
        },
        {
          key: "baseline",
          name: intl.formatMessage({ id: "ML_FORECAST_LEGEND_BASELINE" }),
          color: SERIES_COLORS.baseline,
        },
        {
          key: "ml",
          name: intl.formatMessage({ id: "ML_FORECAST_LEGEND_ML" }),
          color: SERIES_COLORS.ml,
        },
        {
          key: "rob",
          name: intl.formatMessage({ id: "ML_FORECAST_LEGEND_ROB" }),
          color: theme.vars.palette.chart.axis,
          dashed: true,
        },
      ]}
    />
  );
};

export default BookingCurveChart;
